import React, { useEffect, useState } from "react";
import MockTest from "./MockTest";

export default function CustomTestSetup() {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState({});
  const [customConfig, setCustomConfig] = useState({
    total: 0,
    selectedCats: ["ALL"],
    perCategory: {}, // { [cat]: number }
  });
  const [start, setStart] = useState(false);
  const [filtered, setFiltered] = useState([]);

  // Helpers
  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };
  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  // Load questions (skip flagged)
  useEffect(() => {
    let cancelled = false;

    const processQuestions = (questionsArray) => {
      const qs = questionsArray.filter((q) => !q.skip);
      if (cancelled) return;
      setQuestions(qs);

      const catCount = qs.reduce((acc, q) => {
        acc[q.category] = (acc[q.category] || 0) + 1;
        return acc;
      }, {});
      setCategories(catCount);

      // default total = all questions available
      setCustomConfig((prev) => ({
        ...prev,
        total: qs.length,
      }));
    };

    // Use a simple global cache / promise so fetch happens only once
    if (window.__mockQuestionsData) {
      processQuestions(window.__mockQuestionsData);
      return;
    }

    if (window.__mockQuestionsPromise) {
      window.__mockQuestionsPromise
        .then((questionsArray) => processQuestions(questionsArray))
        .catch(() => {});
      return;
    }

    window.__mockQuestionsPromise = fetch(process.env.PUBLIC_URL+ "/mock_questions.json")
      .then((r) => r.json())
      .then((data) => {
        const questionsArray = data.questions || [];
        // cache raw questions array
        window.__mockQuestionsData = questionsArray;
        // clear the promise reference
        delete window.__mockQuestionsPromise;
        return questionsArray;
      });

    window.__mockQuestionsPromise
      .then((questionsArray) => processQuestions(questionsArray))
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  // Restore setup and progress
  useEffect(() => {
    const saved = localStorage.getItem("customTestConfig");
    if (saved) {
      const parsed = JSON.parse(saved);
      setCustomConfig(parsed);
    }

    const started = localStorage.getItem("customTestStarted");
    if (started === "true") {
      const savedQs = localStorage.getItem("customFilteredQuestions");
      if (savedQs) {
        setFiltered(JSON.parse(savedQs));
        setStart(true);
      }
    }
  }, []);

  // Persist setup
  useEffect(() => {
    localStorage.setItem("customTestConfig", JSON.stringify(customConfig));
  }, [customConfig]);

  // Toggle category selection
  const handleCategoryToggle = (cat) => {
    let selected = [...customConfig.selectedCats];
    let perCategory = { ...customConfig.perCategory };

    if (cat === "ALL") {
      // Toggle ALL
      selected = selected.includes("ALL") ? [] : ["ALL"];
      // When ALL is chosen, we don't show per-category inputs; keep perCategory as-is
    } else {
      // Ensure ALL is off
      selected = selected.filter((c) => c !== "ALL");

      if (selected.includes(cat)) {
        // unselect
        selected = selected.filter((c) => c !== cat);
        delete perCategory[cat]; // clean up
      } else {
        // select -> prefill with category total
        selected.push(cat);
        const catTotal = categories[cat] || 0;
        perCategory[cat] = catTotal; // default to full count of that category
      }
    }

    setCustomConfig({ ...customConfig, selectedCats: selected, perCategory });
  };

  // Change per-category value (clamped)
  const handlePerCategoryChange = (cat, raw) => {
    const catTotal = categories[cat] || 0;
    const val = clamp(parseInt(raw, 10) || 0, 0, catTotal);
    setCustomConfig((prev) => ({
      ...prev,
      perCategory: { ...prev.perCategory, [cat]: val },
    }));
  };

  // Start test
  const handleStart = () => {
    const { selectedCats, total, perCategory } = customConfig;
    if (selectedCats.length === 0) {
      alert("Seleccione al menos una categoría.");
      return;
    }

    const allCats = Object.keys(categories);
    const catsToUse =
      selectedCats.includes("ALL") && allCats.length > 0
        ? allCats
        : selectedCats;

    // Determine if perCategory override is in effect (any > 0)
    const hasPerCatOverride = catsToUse.some(
      (cat) => (perCategory[cat] || 0) > 0
    );

    let selectedQs = [];

    if (hasPerCatOverride) {
      // ✅ Per-category OVERRIDES total: pick exactly requested counts
      catsToUse.forEach((cat) => {
        const catQs = questions.filter((q) => q.category === cat);
        const requested = clamp(
          perCategory[cat] || 0,
          0,
          catQs.length
        );
        if (requested > 0) {
          selectedQs.push(...shuffle(catQs).slice(0, requested));
        }
      });
      // Do NOT fill to total — because per-category overrides total
    } else {
      // No per-category provided -> use total from combined pool
      const pool = catsToUse.flatMap((cat) =>
        questions.filter((q) => q.category === cat)
      );
      const desired = clamp(total || 0, 0, pool.length);
      selectedQs = shuffle(pool).slice(0, desired);
    }

    const shuffledQs = shuffle(selectedQs);
    setFiltered(shuffledQs);
    setStart(true);

    // Save progress
    localStorage.setItem("customTestStarted", "true");
    localStorage.setItem(
      "customFilteredQuestions",
      JSON.stringify(shuffledQs)
    );
  };

  const handleReset = () => {
    if (window.confirm("Reiniciar configuracion y evaluacion?")) {
      localStorage.removeItem("customTestConfig");
      localStorage.removeItem("customTestStarted");
      localStorage.removeItem("customFilteredQuestions");
      localStorage.removeItem("mockTestProgress");
      setCustomConfig({
        total: questions.length,
        selectedCats: ["ALL"],
        perCategory: {},
      });
      setStart(false);
      setFiltered([]);
    }
  };

  // If started, render MockTest
  if (start)
    return (
      <div className="container-fluid px-3 px-sm-4 pb-5 quiz-container">
        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-outline-danger" onClick={handleReset}>
            🔄 Reiniciar Configuración
          </button>
        </div>
        <MockTest initialQuestions={filtered} autoStart={true} />
      </div>
    );

  // Setup UI
  return (
    <div className="container-fluid px-3 px-sm-4 pb-5 quiz-container">
      <style>{`
        @media (min-width: 992px) {
          .quiz-container {
            max-width: 900px;
            margin: 0 auto;
          }
        }
        @media (max-width: 768px) {
          .form-check-label, label { font-size: 0.95rem; }
          h5, h6 { font-size: 1rem; }
          button { width: 100%; }
          .form-control { font-size: 0.95rem; }
        }
      `}</style>

      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3">
        <h5 className="mb-2 mb-sm-0">Configurar Evaluación Personalizada</h5>
        <button className="btn btn-outline-danger btn-sm" onClick={handleReset}>
          🔄 Reiniciar Configuracion
        </button>
      </div>

      <div className="mb-3">
        <label htmlFor="totalQuestions" className="form-label">
          Total preguntas:
        </label>
        <input
          id="totalQuestions"
          type="number"
          className="form-control"
          min="1"
          max={questions.length}
          value={customConfig.total}
          onChange={(e) =>
            setCustomConfig({
              ...customConfig,
              total: clamp(parseInt(e.target.value, 10) || 0, 1, questions.length),
            })
          }
        />
        {Object.values(customConfig.perCategory).some((v) => (v || 0) > 0) && (
          <div className="form-text">
            Si se selecciona cantidad por categorias, ese valor se respeta.
          </div>
        )}
      </div>

      <h6>Categories</h6>

      {/* All Categories */}
      <div className="form-check mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id="all-categories"
          checked={customConfig.selectedCats.includes("ALL")}
          onChange={() => handleCategoryToggle("ALL")}
        />
        <label className="form-check-label" htmlFor="all-categories">
          Todas las Categorias
        </label>
      </div>

      {/* Individual categories */}
      {Object.entries(categories).map(([cat, count]) => {
        const checked = customConfig.selectedCats.includes(cat);
        const currentVal =
          customConfig.perCategory[cat] !== undefined
            ? customConfig.perCategory[cat]
            : count; // ✅ default to full count

        return (
          <div className="form-check mb-2" key={cat}>
            <input
              className="form-check-input"
              type="checkbox"
              id={`cat-${cat}`}
              checked={checked}
              onChange={() => handleCategoryToggle(cat)}
            />
            <label className="form-check-label" htmlFor={`cat-${cat}`}>
              {cat} ({count})
            </label>

            {/* Only show per-category input when an individual category is selected
                and "ALL" is not selected */}
            {checked && !customConfig.selectedCats.includes("ALL") && (
              <div className="mt-2">
                <input
                  className="form-control"
                  type="number"
                  min="0"
                  max={count}
                  value={currentVal}
                  onChange={(e) => handlePerCategoryChange(cat, e.target.value)}
                />
                <div className="form-text">
                  Min 0 · Max {count}.
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button className="btn btn-success mt-3" onClick={handleStart}>
        ▶️ Comenzar Evaluacion
      </button>
    </div>
  );
}
